"use client";

import { ButtonOutline } from "@/components/common/ButtonOutline";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { InputPrimary } from "@/components/common/InputPrimary";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import TableSortingIcon from "@/components/common/TableSortingIcon";
import { getAsset } from "@team-shiphouse/shiphouse-sdk";
import { generateImageSrc } from "@/utils/helper";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAppDispatch } from "@/store/hooks";
import { updateInventory } from "@/store/slices/Inventory.slice";
import { Variant } from "@/types/models/product";
import { Label } from "@/components/ui/label";
import { ColumnDef } from "@tanstack/react-table";
import { Pencil, Package, AlertTriangle, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { useState } from "react";
import { formatCurrency } from "@/utils/currency";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";

// Helper function to get stock status
const getStockStatus = (inventory: number) => {
  if (inventory === 0) return { label: "Out of Stock", color: "bg-red-500/20 text-red-600", icon: XCircle };
  if (inventory <= 10) return { label: "Low Stock", color: "bg-orange-500/20 text-orange-600", icon: AlertTriangle };
  return { label: "In Stock", color: "bg-green-500/20 text-green-600", icon: CheckCircle2 };
};

// Helper function to get variant display name from options
const getVariantDisplayName = (variant: Variant): string => {
  const options = [variant?.option1, variant?.option2, variant?.option3]
    .filter((opt) => opt && opt.trim() !== "");
  if (options.length > 0) {
    return options.join(" / ");
  }
  return variant?.title || "Default";
};

export const columns: ColumnDef<Variant>[] = [
  {
    id: "select",
    size: 50,
    minSize: 40,
    maxSize: 60,
    header: ({ table }) => {
      const { rows } = table.getRowModel();
      const selectedRows = table.getSelectedRowModel().rows;
      const isAllSelected = rows.length > 0 && selectedRows.length === rows.length;
      const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

      return (
        <div
          className="flex items-center justify-center transition-opacity duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
            onCheckedChange={(value) => {
              if (value) {
                table.toggleAllPageRowsSelected(true);
              } else {
                table.toggleAllPageRowsSelected(false);
              }
            }}
            aria-label="Select all"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
    cell: ({ row }) => {
      const isSelected = row.getIsSelected();

      return (
        <div
          className={`flex items-center justify-center transition-opacity duration-200 ${
            isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
  },
  {
    accessorKey: "title",
    size: 280,
    minSize: 220,
    maxSize: 400,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Product</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const variant = row.original;
      const imagePath = variant.image || "";
      const imageSrc = imagePath ? generateImageSrc(imagePath, 60, 60) : "";
      const updatedDate = variant.updated_at ? new Date(variant.updated_at) : new Date();
      const formattedDate = format(updatedDate, "MMM dd yyyy");

      return (
        <div className="flex gap-3 items-center">
          <div className="w-10 h-10 overflow-hidden object-cover rounded-md flex-shrink-0">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={variant.title}
                className="w-10 h-10 object-cover"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.style.display = "none";
                }}
              />
            ) : (
              <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
                <Package className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="text-sm font-semibold truncate max-w-[200px]"
              title={variant.title}
            >
              {variant.title}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                {formattedDate}
              </span>
              {variant.product_slug && (
                <>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-muted-foreground font-mono">
                    {variant.product_slug}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "variant",
    size: 180,
    minSize: 150,
    maxSize: 250,
    header: () => <span className="text-base font-bold">Variant</span>,
    cell: ({ row }) => {
      const variant = row.original;
      const variantName = getVariantDisplayName(variant);

      return (
        <div>
          <h3 className="text-sm font-semibold text-blue-600">{variantName}</h3>
          {variant.sku && (
            <span className="text-xs text-gray-600 font-mono">
              SKU: {variant.sku}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "price",
    size: 140,
    minSize: 100,
    maxSize: 180,
    header: ({ column }) => {
      return (
        <div className="group">
          <Button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            variant="ghost"
            className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
          >
            <span className="text-base font-bold">Price</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const variant = row.original;
      const price = variant.price || 0;
      const compareAtPrice = variant.compareAtPrice || 0;

      return (
        <div>
          <h3 className="text-sm font-semibold">{formatCurrency(price)}</h3>
          {compareAtPrice > 0 && compareAtPrice > price && (
            <span className="text-xs text-gray-500 line-through">
              {formatCurrency(compareAtPrice)}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "inventory",
    size: 140,
    minSize: 100,
    maxSize: 180,
    header: ({ column }) => {
      return (
        <div className="group">
          <Button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            variant="ghost"
            className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
          >
            <span className="text-base font-bold">Stock</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const inventory = row.original.inventory || 0;
      const status = getStockStatus(inventory);
      const StatusIcon = status.icon;

      return (
        <div>
          <h3 className="text-sm font-semibold">{inventory} units</h3>
          <div className="flex items-center gap-1 mt-1">
            <StatusIcon className="h-3 w-3" />
            <span className={`text-xs ${status.color.split(' ')[1]}`}>
              {status.label}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    size: 140,
    minSize: 100,
    maxSize: 180,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }) => {
      const inventory = row.original.inventory || 0;
      const status = getStockStatus(inventory);

      return (
        <Badge
          variant="secondary"
          className={`rounded-full px-[10px] py-[5px] ${status.color}`}
        >
          {status.label}
        </Badge>
      );
    },
  },
  {
    accessorKey: "actions",
    size: 200,
    minSize: 150,
    maxSize: 250,
    header: () => <span className="text-base font-bold">Actions</span>,
    cell: ({ row }) => <ActionTab row={row.original} />,
  },
];

const ActionTab: React.FC<{ row: Variant }> = ({ row }) => {
  const [value, setValue] = useState<number>(row.inventory);
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const updateInventoryHandler = () => {
    if (!row.id || !row.product_id) return;
    dispatch(updateInventory({ variant_id: row.id, inventory: value, product_id: row.product_id }));
    setIsOpen(false);
  };

  const handleViewProduct = () => {
    if (!params || !row.product_id) return;
    const storeType = params.storeType as string;
    const storeId = params.storeId as string;
    navigate(`/${storeType}/${storeId}/product/create?q=${row.product_id}`);
  };

  if (!params) return null;

  return (
    <div className="flex items-center gap-2">
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Update inventory">
            <Pencil className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="p-0 overflow-hidden w-[500px]">
          <DialogHeader className="bg-primary h-[70px] p-6">
            <DialogTitle className="text-white text-[18px]">Update Inventory</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 p-6">
            <div className="grid flex-1 gap-2">
              <Label htmlFor="inventory" className="text-sm font-medium">
                Available Inventory
              </Label>
              <InputPrimary
                id="inventory"
                value={value}
                onChange={(e) => setValue(Number(e.target.value))}
                type="number"
                min="0"
              />
              <p className="text-xs text-muted-foreground">
                Current stock: {row.inventory} units
              </p>
            </div>
          </div>
          <DialogFooter className="sm:justify-start p-6 pt-0">
            <DialogClose asChild>
              <ButtonOutline
                type="button"
                className="w-[120px] rounded-full border border-red-500 hover:bg-red-500 hover:text-primary-foreground text-red-500"
              >
                Cancel
              </ButtonOutline>
            </DialogClose>
            <ButtonPrimary
              type="button"
              className="w-[120px] rounded-full"
              onClick={updateInventoryHandler}
            >
              Update
            </ButtonPrimary>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={handleViewProduct}
        title="View parent product"
      >
        <ExternalLink className="h-4 w-4" />
      </Button>
    </div>
  );
};
